/**
 * Header controls for Cached Performance (CR-4751):
 *   - Honest explainer of what the tab is showing
 *   - "Data as of" line tied to the ACTUAL period being displayed
 *   - Single period granularity dropdown (lookback is derived per period)
 *   - Current scope label (derived from ViewContext)
 */
import { Filter, Info } from 'lucide-react'

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select'
import type { PeriodType } from '../../../lib/api/dashboard-snapshots'
import { formatDate } from './helpers'
import { useFormatters } from '../../../contexts/PreferencesContext'

interface CachedHeaderProps {
  period: PeriodType
  onPeriodChange: (p: PeriodType) => void
  /** snapshot_date of the latest snapshot returned for the selected period */
  latestSnapshotDate: string | null
  /** period_end of the latest period snapshot shown (null for state metrics) */
  latestPeriodEnd: string | null
  scopeLabel: string | null
}

const PERIOD_NOUN: Record<PeriodType, string> = {
  daily: 'day', weekly: 'week', monthly: 'month', quarterly: 'quarter',
}

const PERIOD_HINT: Record<PeriodType, string> = {
  daily: 'last 30 days',
  weekly: 'last 13 weeks',
  monthly: 'last 12 months',
  quarterly: 'last 8 quarters',
}

export function CachedHeader({
  period, onPeriodChange,
  latestSnapshotDate, latestPeriodEnd, scopeLabel,
}: CachedHeaderProps) {
  const { formatDate: ctxFormatDate } = useFormatters()
  // Prefer period_end over snapshot_date — period_end is what users expect
  // ("data for the month ending 31 Mar 2026").
  const asOf = latestPeriodEnd ?? latestSnapshotDate
  const asOfLabel = asOf ? formatDate(asOf, ctxFormatDate) : null

  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="w-3 h-3" />
          Nightly KPI snapshots. Period metrics (OTIF, PPM, spend) are frozen at capture; state metrics (open orders, arrears) reflect as-of values.
        </p>
        {/* CR-4751: the filter bar above belongs to the live tabs; snapshots
            are captured per site and group, not per filter. */}
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <Filter className="w-3 h-3" />
          Filters don&apos;t apply to cached snapshot data.
        </p>
        <div className="flex flex-wrap gap-4 mt-1 text-xs text-muted-foreground">
          {asOfLabel && (
            <span>
              Showing latest complete {PERIOD_NOUN[period]}: <span className="font-medium">{asOfLabel}</span>
            </span>
          )}
          {scopeLabel && <span>Scope: <span className="font-medium">{scopeLabel}</span></span>}
          <span>Window: {PERIOD_HINT[period]}</span>
        </div>
      </div>
      <div>
        <Select value={period} onValueChange={(v) => onPeriodChange(v as PeriodType)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
